import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User,
  LogOut,
  Star,
  MapPin,
  Clock,
  Shield,
  Award,
  Plane,
  Bell,
  ChevronRight,
  Zap,
  Home,
  Bookmark,
  Users,
  Settings,
  Crown,
  MessageSquare,
  Phone,
  Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import FloatingSupport from '../components/FloatingSupport';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const TIER_CONFIG = {
  free: { label: 'Explorer', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: '🌱' },
  basic: { label: 'Traveler', color: 'text-teal-600', bgColor: 'bg-teal-100', icon: '✈️' },
  pro: { label: 'Pro', color: 'text-cyan-600', bgColor: 'bg-cyan-100', icon: '⭐' },
  premium: { label: 'Premium', color: 'text-amber-600', bgColor: 'bg-amber-100', icon: '💎' },
};

export default function Profile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, needsOnboarding } = useAuth();
  
  const [trustScore, setTrustScore] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (needsOnboarding) {
      navigate('/onboarding');
      return;
    }
    
    const loadProfileData = async () => {
      if (!user?.user_id) return;
      
      try {
        const [trustData, subData, plansData] = await Promise.all([
          api.getTrustScore(user.user_id).catch(() => ({ total_score: 0 })),
          api.getSubscriptionStatus(user.user_id).catch(() => ({ tier: 'free', plans_remaining: 2 })),
          api.getSavedPlans(user.user_id).catch(() => ({ plans: [] })),
        ]);
        
        setTrustScore(trustData);
        setSubscription(subData);
        setSavedPlans(plansData.plans || []);
      } catch (error) {
        console.error('Profile data load error:', error);
      }
    };
    
    loadProfileData();
  }, [isAuthenticated, needsOnboarding, navigate, user]);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  if (!isAuthenticated || !user) return null;
  
  const tier = subscription?.tier || 'free';
  const tierInfo = TIER_CONFIG[tier] || TIER_CONFIG.free;
  const scorePercent = trustScore?.total_score || 0;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="home-btn"
          >
            <Home size={20} />
          </Link>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-8 h-8" />
            <span className="font-bold text-gray-900">Profile</span>
          </div>
          
          <button 
            onClick={handleLogout}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            style={{ borderRadius: '50%' }}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Profile Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow-lg"
          style={{ borderRadius: '1.5rem' }}
        >
          <div className="flex items-center gap-5">
            {user.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-20 h-20 border-4 border-teal-100 shadow-lg"
                style={{ borderRadius: '1.25rem' }}
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg" style={{ borderRadius: '1.25rem' }}>
                <User size={36} className="text-white" />
              </div>
            )}
            
            <div className="flex-1">
              <h2 className="text-2xl font-black text-gray-900">{user.name || 'Traveler'}</h2>
              <p className="text-gray-500">{user.email}</p>
              
              <div className="flex items-center gap-3 mt-3">
                <span className={`px-3 py-1 text-sm font-bold ${tierInfo.bgColor} ${tierInfo.color}`} style={{ borderRadius: '9999px' }}>
                  {tierInfo.icon} {tierInfo.label}
                </span>
                {subscription?.plans_remaining !== undefined && (
                  <span className="text-xs text-gray-400">
                    {subscription.plans_remaining} plans left
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Trust Score */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow-lg"
          style={{ borderRadius: '1.5rem' }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
              <Shield size={18} className="text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Travel DNA Score</h3>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Score Ring */}
            <div className="relative w-24 h-24">
              <svg className="w-full h-full -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  fill="none"
                  stroke="url(#scoreGradient)"
                  strokeWidth="8"
                  strokeDasharray={`${scorePercent * 2.51} 251`}
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#14b8a6" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-black text-gray-900">{scorePercent}</span>
              </div>
            </div>
            
            <div className="flex-1">
              <p className="text-gray-500 text-sm mb-3">
                Your travel DNA unlocks personalized recommendations and priority access.
              </p>
              <Link 
                to="/onboarding"
                className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold hover:text-teal-500 transition-colors"
                data-testid="improve-score-btn"
              >
                Complete Travel DNA to improve
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </motion.div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: MapPin, value: savedPlans.length, label: 'Plans', color: 'teal' },
            { icon: Clock, value: trustScore?.breakdown?.plans_completed || 0, label: 'Completed', color: 'cyan' },
            { icon: Star, value: trustScore?.breakdown?.reviews_submitted || 0, label: 'Reviews', color: 'amber' },
          ].map((stat, index) => (
            <motion.div 
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.05 }}
              className="p-4 bg-white/80 backdrop-blur border border-gray-100 shadow text-center"
              style={{ borderRadius: '1rem' }}
            >
              <div className={`w-10 h-10 mx-auto mb-2 flex items-center justify-center ${
                stat.color === 'teal' ? 'bg-teal-100' : 
                stat.color === 'cyan' ? 'bg-cyan-100' : 'bg-amber-100'
              }`} style={{ borderRadius: '0.75rem' }}>
                <stat.icon size={18} className={
                  stat.color === 'teal' ? 'text-teal-600' : 
                  stat.color === 'cyan' ? 'text-cyan-600' : 'text-amber-600'
                } />
              </div>
              <p className="text-2xl font-black text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </motion.div>
          ))}
        </div>
        
        {/* Quick Links */}
        <div className="space-y-2">
          {[
            { to: '/saved', icon: Bookmark, label: 'Saved Plans', badge: savedPlans.length || null, color: 'teal' },
            { to: '/notifications', icon: Bell, label: 'Notifications', color: 'cyan' },
            { to: '/onboarding', icon: Award, label: 'Travel DNA', subtitle: 'Personalize your experience', color: 'amber' },
            { to: '/community', icon: Users, label: 'Community', subtitle: 'Reviews & Events', color: 'rose' },
          ].map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="flex items-center gap-4 p-4 bg-white/80 backdrop-blur border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all group"
              style={{ borderRadius: '1rem' }}
            >
              <div className={`w-12 h-12 flex items-center justify-center group-hover:scale-110 transition-transform ${
                link.color === 'teal' ? 'bg-teal-100' : 
                link.color === 'cyan' ? 'bg-cyan-100' : 
                link.color === 'amber' ? 'bg-amber-100' : 'bg-rose-100'
              }`} style={{ borderRadius: '0.75rem' }}>
                <link.icon size={22} className={
                  link.color === 'teal' ? 'text-teal-600' : 
                  link.color === 'cyan' ? 'text-cyan-600' : 
                  link.color === 'amber' ? 'text-amber-600' : 'text-rose-600'
                } />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{link.label}</p>
                {link.subtitle && <p className="text-sm text-gray-500">{link.subtitle}</p>}
              </div>
              {link.badge && (
                <span className="px-2.5 py-1 bg-teal-100 text-teal-600 text-xs font-bold" style={{ borderRadius: '9999px' }}>
                  {link.badge}
                </span>
              )}
              <ChevronRight size={18} className="text-gray-300 group-hover:text-teal-500 transition-colors" />
            </Link>
          ))}
        </div>
        
        {/* Upgrade CTA */}
        {tier === 'free' && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-200 shadow-lg"
            style={{ borderRadius: '1.5rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg" style={{ borderRadius: '1rem' }}>
                <Crown size={24} className="text-white" />
              </div>
              <div>
                <span className="font-black text-xl text-gray-900">Upgrade to Pro</span>
                <p className="text-gray-500 text-sm">Unlock all features</p>
              </div>
            </div>
            
            {/* Pro Features */}
            <div className="space-y-2 mb-5">
              {[
                { icon: MessageSquare, text: 'Trip-specific chat with travel buddies' },
                { icon: Phone, text: 'Contact exchange (with consent)' },
                { icon: Zap, text: 'Priority in travel buddy matching' },
                { icon: Shield, text: 'Unlimited trip posts' },
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                  <Check size={16} className="text-emerald-500" />
                  {feature.text}
                </div>
              ))}
            </div>
            
            {/* Pricing Options */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Link
                to="/payment?plan=monthly"
                className="p-3 bg-white border-2 border-gray-200 hover:border-amber-400 transition-all text-center"
                style={{ borderRadius: '1rem' }}
              >
                <p className="text-xl font-black text-gray-900">₹299</p>
                <p className="text-xs text-gray-500">/month</p>
              </Link>
              <Link
                to="/payment?plan=annual"
                className="p-3 bg-amber-100 border-2 border-amber-300 hover:border-amber-500 transition-all text-center relative"
                style={{ borderRadius: '1rem' }}
              >
                <span className="absolute -top-2 right-2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold" style={{ borderRadius: '9999px' }}>
                  44% off
                </span>
                <p className="text-xl font-black text-gray-900">₹1,999</p>
                <p className="text-xs text-gray-500">/year</p>
              </Link>
            </div>
            
            <Link
              to="/payment?plan=monthly"
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white font-bold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all"
              style={{ borderRadius: '9999px' }}
              data-testid="upgrade-btn"
            >
              <Crown size={18} />
              Upgrade Now
            </Link>
          </motion.div>
        )}
        
        {/* Pro Member Badge */}
        {tier === 'pro' && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200"
            style={{ borderRadius: '1.5rem' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center" style={{ borderRadius: '1rem' }}>
                <Crown size={24} className="text-white" />
              </div>
              <div>
                <span className="font-black text-xl text-gray-900">Pro Member</span>
                <p className="text-gray-600 text-sm">All features unlocked</p>
              </div>
            </div>
          </motion.div>
        )}
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-teal-600">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      <FloatingSupport />
    </div>
  );
}
